export const LIMITS = {
  maxFiles: 25,
  maxFileBytes: 25 * 1024 * 1024,
  maxBatchBytes: 100 * 1024 * 1024,
  maxMegapixels: 50,
  maxLongEdge: 2400,
} as const;

export type PageSize = 'match' | 'letter' | 'a4';
export type FitMode = 'contain' | 'fill';
export type Quality = 72 | 84 | 92;

export type Size = {
  width: number;
  height: number;
};

export type Placement = Size & {
  x: number;
  y: number;
};

export const LETTER_PORTRAIT: Size = { width: 612, height: 792 };
export const A4_PORTRAIT: Size = { width: 595.28, height: 841.89 };
export const PAGE_MARGIN = 18;

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  const decimals = exponent === 0 || value >= 10 ? 0 : 1;
  return `${value.toFixed(decimals)} ${units[exponent]}`;
}

export function sanitizePdfFilename(value: string): string {
  const withoutExtension = value.trim().replace(/\.pdf$/i, '');
  const withoutControls = Array.from(withoutExtension, (character) => (
    character.charCodeAt(0) < 32 ? '-' : character
  )).join('');
  const cleaned = withoutControls
    .replace(/[<>:"/\\|?*]/g, '-')
    .replace(/\s+/g, ' ')
    .replace(/[. ]+$/g, '')
    .trim()
    .slice(0, 100);
  return `${cleaned || 'photos'}.pdf`;
}

export function isSupportedPhoto(file: Pick<File, 'name' | 'type'>): boolean {
  const allowedTypes = new Set(['image/jpeg', 'image/png', 'image/webp']);
  const extension = file.name.split('.').pop()?.toLowerCase();
  return allowedTypes.has(file.type.toLowerCase()) || ['jpg', 'jpeg', 'png', 'webp'].includes(extension ?? '');
}

export function validateFilePolicy(
  file: Pick<File, 'name' | 'type' | 'size'>,
  acceptedCount: number,
  acceptedBytes: number,
): string | null {
  if (!isSupportedPhoto(file)) return `${file.name}: use a JPG, PNG, or WebP photo.`;
  if (file.size > LIMITS.maxFileBytes) return `${file.name}: larger than the 25 MB per-photo limit.`;
  if (acceptedCount >= LIMITS.maxFiles) return `${file.name}: the 25-photo limit is already reached.`;
  if (acceptedBytes + file.size > LIMITS.maxBatchBytes) {
    return `${file.name}: adding it would exceed the 100 MB batch limit.`;
  }
  return null;
}

export function megapixels(width: number, height: number): number {
  return (width * height) / 1_000_000;
}

export function rotatedSize(width: number, height: number, rotation: number): Size {
  const normalized = ((rotation % 360) + 360) % 360;
  return normalized === 90 || normalized === 270
    ? { width: height, height: width }
    : { width, height };
}

export function constrainedCanvasSize(width: number, height: number, maxLongEdge = LIMITS.maxLongEdge): Size {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxLongEdge) return { width, height };
  const scale = maxLongEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

export function resolvePageSize(pageSize: PageSize, imageWidth: number, imageHeight: number): Size {
  if (pageSize === 'match') {
    const cssToPoints = 0.75;
    const natural = {
      width: imageWidth * cssToPoints,
      height: imageHeight * cssToPoints,
    };
    const cap = 841.89;
    const scale = Math.min(1, cap / Math.max(natural.width, natural.height));
    return {
      width: Math.max(72, natural.width * scale),
      height: Math.max(72, natural.height * scale),
    };
  }

  const portrait = pageSize === 'letter' ? LETTER_PORTRAIT : A4_PORTRAIT;
  if (imageWidth > imageHeight) {
    return { width: portrait.height, height: portrait.width };
  }
  return portrait;
}

export function calculatePlacement(
  image: Size,
  page: Size,
  fitMode: FitMode,
  margin = PAGE_MARGIN,
): Placement {
  const availableWidth = Math.max(1, page.width - margin * 2);
  const availableHeight = Math.max(1, page.height - margin * 2);
  const containScale = Math.min(availableWidth / image.width, availableHeight / image.height);
  const fillScale = Math.max(page.width / image.width, page.height / image.height);
  const scale = fitMode === 'contain' ? containScale : fillScale;
  const width = image.width * scale;
  const height = image.height * scale;
  return {
    width,
    height,
    x: (page.width - width) / 2,
    y: (page.height - height) / 2,
  };
}
